import { Reaction } from 'getstream';
import { PrivacyTypesEnum, UserToEntityRelationVisibilityEnum } from '../../enums';
import { FeedReactionKindsEnum, FeedVerbEnum, FeedWebhookTypesEnum } from '../../enums/getstresm-feed.enum';
import { IsArray, IsDateString, IsEnum, IsOptional, IsString, IsUUID, ValidateNested } from 'class-validator';
import { Type } from 'class-transformer';
import { PastEventDto } from './past-event.dto';
import { InterestDto } from './interest.dto';
import { FileDto, FileUploadDto } from './file.dto';

export class ShortActivityDto {
	id: string;
	foreign_id: string;
	time: string;
}

export class ActivityDto {
	public id: string;

	public authorId: string;
	public participationPrivacyId: PrivacyTypesEnum;

	public pastEventId: string;
	public name?: string;
	public pastEventName: string;
	public eventVisibilityId: UserToEntityRelationVisibilityEnum;
	public startedAt: Date;
	public addressLat?: string;
	public addressLng?: string;
	public addressName?: string;
	public isOnlineEvent: boolean;
	public fitnessTypeId: string;
	public masteryLevel?: number;

	public comment?: string;
	public isWorkout: boolean;

	public createdAt: string;
	public updatedAt: string;

	public pastEvent?: PastEventDto;
	public fitnessType?: InterestDto;
}

export class ActivityAttachmentDto
implements Pick<FileDto,
	'id' | 'mimeType' | 'fileName' | 'key' | 'size' | 'url'
> {
	public id: string;
	public mimeType: string;

	public fileName: string;
	public size: number;
	public key: string;

	public url?: string;
}

export class CreateActivityDto
	implements Omit<ActivityDto, 'id' | 'authorId' | 'isWorkout' | 'attachments' | 'createdAt' | 'updatedAt'>
{
	public id?: string;

	public participationPrivacyId: PrivacyTypesEnum;

	public pastEventId: string;
	public pastEventName: string;
	public eventVisibilityId: UserToEntityRelationVisibilityEnum;
	public startedAt: Date;
	public addressLat?: string;
	public addressLng?: string;
	public addressName?: string;
	public isOnlineEvent: boolean;
	public onlineUrl?: string;

	public fitnessTypeId: string;
	public masteryLevel?: number;

	public comment?: string;
	public isWorkout?: boolean;

	public createdAt?: string | Date;
	public updatedAt?: string | Date;
}

export class AddActivityDto {
	@IsUUID()
	authorId: string;

	@IsEnum(FeedVerbEnum)
	verb: FeedVerbEnum;

	@IsOptional()
	@IsString()
	foreign_id?: string;

	@IsOptional()
	@IsDateString()
	time?: string;

	@Type(() => CreateActivityDto)
	@ValidateNested()
	object: CreateActivityDto;

	@IsOptional()
	@IsArray()
	@Type(() => ActivityAttachmentDto)
	@ValidateNested({ each: true })
	attachments?: ActivityAttachmentDto[];

	@IsOptional()
	_created_on_server: boolean;
}

export class WebhookReactionDto {
	app_id: number;
	type: string | FeedWebhookTypesEnum;
	published_at: string;
	feed?: string;
	generic: {
		id: string;
		parent?: string;
		kind: FeedReactionKindsEnum;
		user_id: string;
		data: ReactionDto;
		activity_id: string;
		created_at: string;
		updated_at: string;
	};
}

export class WebhookActivityDto {
	app_id: number;
	published_at: string;
	feed: string;
	deleted: string[];
	deleted_foreign_ids: [string, string][];
	new: GenericActivityDto[];
}

export interface GenericActivityDto {
	id: string;
	actor: string;
	verb: FeedVerbEnum;
	object: string | any;
	target: null | string;
	origin: null | string;
	time: string;
	foreign_id: string | null;
	attachments?: ActivityAttachmentDto[];

	_created_on_server?: boolean;
}

export class ReactionDto {
	public id: string;
	public kind: FeedReactionKindsEnum;
	public feedUserId: string;
	public activityId: string;
	public authorId: string;
	public comment?: string;
	public commentId?: string;
	public emoji?: string;
	public mentions?: MentionDto[];
	public createdAt: string;
	public updatedAt: string;
}

export type MentionDto = {
	id: string;
	userName: string;
};

export class AddReactionDto {
	@IsEnum(FeedReactionKindsEnum)
	public kind: FeedReactionKindsEnum;

	public feedUserId: string;
	public activityId: string;
	public comment?: string;
	public commentId?: string;
	public replyId?: string;
	public emoji?: string;
}

export type GenericFeedReactionDto = Reaction<ReactionDto & { [key: string]: any }>;

export type AddCommonActivity = {
	verb: FeedVerbEnum;
	feedUserId?: string;
	object: any;
	foreign_id?: string;
	time?: string;
	otherFields?: any;

	imagesToUpload?: FileUploadDto[];
	videosToUpload?: FileUploadDto[];
};

export class UpdateCommonActivity {
	feedUserId: string;
	activityId: string;
	foreign_id: string;
	time: string;

	object: any;

	imagesToUpload?: FileUploadDto[];
	videosToUpload?: FileUploadDto[];
	fileIdsToDelete?: string[];
}
